import React from 'react'
import clsx from 'clsx'
import { IconButton, useTheme } from '@mui/material'
import { More } from 'iconsax-react'

import CollapseButton from '@SonarCX/components/atoms/CollapseButton'
import LoadingSpinner from '@SonarCX/components/atoms/LoadingSpinner'
import ButtonMenu from '@SonarCX/components/molecules/ButtonMenu'

import { useResponsiveSettings } from '@SonarCX/providers/ResponsiveSettingsProvider'
import useStyles from './styles'

interface SettingsSectionProps {
  title?: string
  loaded: boolean
  expandable?: boolean
  sort?: (ascending: boolean) => void
  children: React.ReactNode
  className?: string
}

const SORT_ASC = 'Sort ascending'
const SORT_DESC = 'Sort descending'

const SettingsSection: React.FC<SettingsSectionProps> = ({
  title,
  loaded,
  expandable,
  children,
  sort,
  className
}) => {
  const theme = useTheme()
  const { isSmall, isExtraSmall } = useResponsiveSettings()
  const [expanded, setExpanded] = React.useState(true)
  const anchorRef = React.useRef<HTMLDivElement>(null)
  const detailsMenuOpts = React.useMemo(() => [SORT_ASC, SORT_DESC], [])

  const classes = useStyles({ isSmall, isExtraSmall, expanded, expandable })

  const handleMenuSelect = React.useCallback(
    (option: string) => {
      if (option === SORT_ASC) {
        sort(true)
      } else {
        sort(false)
      }
    },
    [sort]
  )

  return (
    <div className={clsx(classes.root, className)} ref={anchorRef}>
      {title && (
        <div className={classes.title}>
          {expandable && (
            <CollapseButton
              open={expanded}
              onToggle={() => setExpanded(!expanded)}
              className={classes.button}
            />
          )}
          {sort && (
            <ButtonMenu
              options={detailsMenuOpts}
              onSelect={handleMenuSelect}
              targetRef={anchorRef}
            >
              <IconButton>
                <More
                  color='black'
                  size={theme.size.base}
                  style={{
                    transform: 'rotate(90deg)'
                  }}
                />
              </IconButton>
            </ButtonMenu>
          )}
          {title}
        </div>
      )}

      {expanded && (
        <div className={classes.children}>
          {loaded ? children : <LoadingSpinner />}
        </div>
      )}
    </div>
  )
}

export default SettingsSection
